import React, { FC, useEffect, useState, useCallback } from 'react';
import { useEnvironment, WidgetProps, useBi } from '@wix/yoshi-flow-editor';
import { faqAppLoads, faqQuestionExpanded } from '@wix/bi-logger-labs-data/v2';
import { pauseHeightChanges } from '@wix/yoshi-flow-editor/wix-height-updater';
import { TPAComponentsProvider } from 'wix-ui-tpa/cssVars';
import { ISettingsContextValue } from '@wix/tpa-settings';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import { st, classes } from './Widget.st.css';

import HeaderWrapper from './HeaderWrapper/HeaderWrapper';
import TabsWrapper from './Tabs/TabsWrapper';
import QuestionsWrapper from './QuestionsWrapper/QuestionsWrapper';
import SearchWrapper from './SearchWrapper/SearchWrapper';
import { AppContext } from './SettingsProvider/SettingsProvider';
import settingsParams from '../settingsParams';
import { CategoriesPosition } from '../../../enums';
import constants from '../../../constants';
import { AccordionCSS } from './QuestionsWrapper/Interfaces';
import { QuestionEntry } from '@wix/ambassador-faq-question-entry-v2-question-entry/types';
import { ErrorMessage } from './ErrorHandler/ErrorMessage';
import { DATA_HOOKS } from '../../../constants/dataHooksElements';
import stylesParams from '../stylesParams';
import { ControllerProps } from '../../../types/ControllerProps';

const Widget: FC<WidgetProps<ControllerProps>> = ({
  instance,
  instanceId,
  baseURL,
  api: appAPI,
  initialData,
  questionId,
  fromSearch,
  host,
  listCategoriesResponse,
  listQuestionsResponse,
  fetchError,
  searchTermResponse,
  isSEO = false,
  isHasQuestions = true,
  isEditor3 = false,
}) => {
  const logger = useBi();
  const settings: ISettingsContextValue = useSettings();
  const styles = useStyles();
  const {
    isMobile,
    isEditor,
    multilingual,
    isEditorX: isResponsive,
  } = useEnvironment();
  // Handle RTL languages
  let isRTL = settings.get(settingsParams.textAlignment) === 'right';
  if (multilingual?.isEnabled) {
    const languageCode = multilingual.currentLanguage?.split('-')[0] || '';
    isRTL = constants.RTL_LANGUAGE.includes(languageCode);
  }

  // Extract settings values

  let vertical, horizontal;

  if (isResponsive && isMobile) {
    const spacing = settings.get(settingsParams.spacing);
    if (spacing.vertical === 0 && spacing.horizontal === 0) {
      vertical = 0;
      horizontal = 20;
    } else {
      ({ vertical, horizontal } = spacing);
    }
  } else {
    ({ vertical, horizontal } = settings.get(settingsParams.spacing));
  }

  const categoriesPosition: CategoriesPosition = settings.get(
    settingsParams.categoriesPosition,
  );

  const showTitleStyleParam = styles.get?.(stylesParams.showTitle) ?? true;
  const showSearchStyleParam = styles.get?.(stylesParams.showSearchBar) ?? true;
  const showSearch =
    showSearchStyleParam && settings.get(settingsParams.searchBar).show;
  const showTitle =
    showTitleStyleParam && settings.get(settingsParams.title).show;
  const showFirstAnswer = settings.get(settingsParams.openFirstAnswer);
  const itemAppearance = settings.get(settingsParams.itemAppearance);

  // Define component states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(
    initialData?.selectedCategory,
  );
  const [firstQuestionId, setFirstQuestionId] = useState<string | undefined>(
    questionId,
  );

  const handleSearchChange = useCallback(
    (term: string) => setSearchTerm(term),
    [],
  );

  // Adjust styles based on settings
  const customStyle: AccordionCSS = {
    '--item-top-bottom-padding': `${vertical}px`,
    '--item-left-right-padding': `${horizontal}px`,
  };
  const showCategoriesSettingsParam =
    settings.get(settingsParams.displayContent) ===
    constants.DISPLAY_CONTENT.SPLIT_INTO_CATEGORIES;
  const showCategoriesStyleParam =
    styles.get?.(stylesParams.showFAQCategories) ?? true;
  const showCategories =
    (showCategoriesSettingsParam && showCategoriesStyleParam) ||
    searchTerm !== '';
  const isSideCategories =
    categoriesPosition === constants.DISPLAY_CATEGORIES.SIDE;
  const isTopCategories =
    categoriesPosition === constants.DISPLAY_CATEGORIES.TOP;

  useEffect(() => pauseHeightChanges(), [host]);

  // Log analytics event on load
  useEffect(() => {
    logger.report(
      faqAppLoads({
        num_of_categories_shown: initialData.validLists?.length ?? 0,
        num_of_categories:
          settings.get(settingsParams.manageCategories).categories?.length ?? 0,
        num_of_questions: initialData.questionEntries?.length ?? 0,
        layout_type: settings.get(settingsParams.layout),
        columns: settings.get(settingsParams.structure),
        category_placement: settings.get(settingsParams.categoriesPosition),
        isOoi: true,
      }),
    );
  }, [initialData, logger, settings]);

  // Handle deep linking and scrolling to the question
  useEffect(() => {
    if (isEditor) {
      setFirstQuestionId(showFirstAnswer ? questionId : undefined);
      return;
    }

    if (!questionId || !fromSearch) {
      return;
    }

    const { questionEntries } = initialData;
    const questionData = questionEntries?.find(
      (q: QuestionEntry) => q.id === questionId,
    );

    if (questionData) {
      const { question, categoryId, sortOrder } = questionData;
      logger.report(
        faqQuestionExpanded({
          type: 'deep link',
          question_id: questionId,
          question_name: question || '',
          category_id: categoryId || '',
          question_order: sortOrder || 0,
          url: baseURL || '',
          isOoi: true,
        }),
      );
    }

    const element = document.getElementById(questionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.focus();
    }
  }, [
    fromSearch,
    isEditor,
    questionId,
    showFirstAnswer,
    initialData,
    logger,
    baseURL,
  ]);

  return (
    <TPAComponentsProvider value={{ rtl: isRTL, mobile: isMobile, seo: isSEO }}>
      <AppContext.Provider
        value={{
          instance,
          instanceId,
          baseURL,
          selectedCategory,
          setSelectedCategory,
          firstQuestionId,
          setFirstQuestionId,
          questionId,
          appAPI,
          searchTerm,
          setSearchTerm,
          listCategoriesResponse,
          listQuestionsResponse,
          searchTermResponse,
          fetchError,
          isEditor3,
        }}
      >
        <div
          data-hook={DATA_HOOKS.WIDGET_ROOT}
          dir={isRTL ? 'rtl' : 'ltr'}
          style={customStyle}
          className={st(classes.container, {
            mobile: isMobile,
            isSideCategoriesPosition:
              searchTerm !== '' ? false : isSideCategories,
            isTopCategoriesPosition: isTopCategories || searchTerm !== '',
            hideSearch: !showSearch || !isHasQuestions,
            hideTitle: !showTitle || !isHasQuestions,
            hideCategories: !showCategories || !isHasQuestions,
            theme: itemAppearance,
          })}
        >
          {isHasQuestions && <HeaderWrapper />}
          {isHasQuestions && (
            <SearchWrapper onSearchChange={handleSearchChange} />
          )}
          {isHasQuestions && (
            <TabsWrapper validLists={initialData.validLists} />
          )}
          <QuestionsWrapper questions={initialData.questionEntries} />
          <ErrorMessage error={fetchError} />
        </div>
      </AppContext.Provider>
    </TPAComponentsProvider>
  );
};

export default Widget;
